"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.KeitaroService = exports.upsertStreamToCampaign = exports.resolveOfferwallCloneRouting = exports.resolveOfferwallTemplateCampaign = void 0;
const http_1 = __importDefault(require("./http"));
const app_1 = require("../../app/app");
const keitaro_utils_1 = require("../../utils/keitaro-utils");
const general_1 = require("../../utils/general");
const app_campaign_naming_1 = require("../../app/app-campaign-naming");
const DEFAULT_OFFERWALL_TEMPLATE_CAMPAIGN_BY_APP_PLATFORM = {
    android: { id: 3526, name: 'Android' },
    ios: { id: 3521, name: 'iOS' }
};
function readPositiveCampaignId(value, fallback) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}
function resolveOfferwallTemplateCampaign(appPlatform, env = process.env) {
    const normalizedPlatform = String(appPlatform || '').trim().toLowerCase();
    const fallback = DEFAULT_OFFERWALL_TEMPLATE_CAMPAIGN_BY_APP_PLATFORM[normalizedPlatform];
    if (!fallback) {
        throw new Error(`Unsupported offerwall appPlatform "${appPlatform}"`);
    }
    const envKey = normalizedPlatform === 'android'
        ? 'OFFERWALL_TEMPLATE_ANDROID_CAMPAIGN_ID'
        : 'OFFERWALL_TEMPLATE_IOS_CAMPAIGN_ID';
    return {
        ...fallback,
        id: readPositiveCampaignId(env[envKey], fallback.id),
    };
}
exports.resolveOfferwallTemplateCampaign = resolveOfferwallTemplateCampaign;
function resolveOfferwallCloneRouting(originalCampaign, domains, random = Math.random) {
    const safeDomains = (domains || []).filter((domain) => Number(domain?.id || 0) > 0);
    const selectedDomain = safeDomains.length > 0
        ? safeDomains[Math.min(safeDomains.length - 1, Math.max(0, Math.floor(random() * safeDomains.length)))]
        : undefined;
    const domainId = Number(selectedDomain?.id || originalCampaign.domain_id || 0);
    const trafficSourceId = Number(originalCampaign.traffic_source_id || 0);
    return {
        ...(domainId > 0 ? { domain_id: domainId } : {}),
        group_id: Number(originalCampaign.group_id || 0),
        ...(trafficSourceId > 0 ? { traffic_source_id: trafficSourceId } : {}),
    };
}
exports.resolveOfferwallCloneRouting = resolveOfferwallCloneRouting;
function getOfferwallAppPlatform(app) {
    const rawPlatform = app?.appPlatform;
    if (typeof rawPlatform !== 'string') {
        throw new Error(`Offerwall appPlatform is required for app #${app.id}`);
    }
    const normalizedPlatform = rawPlatform.trim().toLowerCase();
    if (!DEFAULT_OFFERWALL_TEMPLATE_CAMPAIGN_BY_APP_PLATFORM[normalizedPlatform]) {
        throw new Error(`Unsupported offerwall appPlatform "${rawPlatform}" for app #${app.id}`);
    }
    return normalizedPlatform;
}
async function getStreamsByCampaignId(campaignId) {
    const { data: streams } = await http_1.default.get(`campaigns/${campaignId}/streams`);
    return streams;
}
async function getAllCampaigns() {
    const { data: campaigns } = await http_1.default.get('campaigns');
    return campaigns;
}
async function cloneStreams(originalCampaignId, streamPositionsToClone, campaignRegExp) {
    // Get the original campaign's streams
    const originalStreams = await getStreamsByCampaignId(originalCampaignId);
    // Filter the original streams by the given stream positions to clone
    const streamsToClone = originalStreams.filter(stream => streamPositionsToClone.includes(stream.position));
    // Get a list of all campaigns
    const allCampaigns = await getAllCampaigns();
    // Filter the campaigns by the given RegExp
    const matchingCampaigns = allCampaigns.filter(campaign => campaignRegExp.exec(campaign.name) && campaign.id != originalCampaignId);
    // console.log(matchingCampaigns)
    // For each matching campaign, clone the streams
    for (const matchingCampaign of matchingCampaigns) {
        for (const streamToClone of streamsToClone) {
            await http_1.default.post('streams', {
                name: streamToClone.name,
                campaign_id: matchingCampaign.id,
                schema: streamToClone.schema,
                type: streamToClone.type,
                action_type: streamToClone.action_type,
                weight: 100,
                offers: streamToClone.offers.map((offer) => {
                    return {
                        offer_id: offer.offer_id,
                        share: 100
                    };
                }),
                filters: streamToClone.filters.map((filter) => {
                    return {
                        name: filter.name,
                        mode: filter.mode,
                        payload: filter.payload
                    };
                }),
            });
        }
    }
}
async function updateCampaign(id, payload) {
    await http_1.default.put(`campaigns/${id}`, payload);
}
async function createStreamForMatchingCampaigns(streamPayloads, campaignRegExp, avoidGroup) {
    // Get all campaigns once
    const allCampaigns = await getAllCampaigns();
    const matchingCampaigns = allCampaigns.filter(campaign => (avoidGroup ? campaign.group_id != avoidGroup : true) &&
        campaignRegExp.exec(campaign.name));
    // Get all streams for matching campaigns once
    const campaignStreams = new Map();
    for (const campaign of matchingCampaigns) {
        campaignStreams.set(campaign.id, await getStreamsByCampaignId(campaign.id));
    }
    // Process each campaign
    for (const matchingCampaign of matchingCampaigns) {
        const streams = campaignStreams.get(matchingCampaign.id) || [];
        // Process each stream payload
        for (const { payload, offerId } of streamPayloads) {
            const identicalStream = streams.find(stream => stream.name.includes(offerId));
            try {
                if (identicalStream) {
                    await http_1.default.put(`streams/${identicalStream.id}`, {
                        campaign_id: matchingCampaign.id,
                        ...payload
                    });
                }
                else {
                    await http_1.default.post('streams', {
                        campaign_id: matchingCampaign.id,
                        ...payload
                    });
                }
            }
            catch (error) {
                console.error(`Error updating stream ${offerId} (${payload.name}) in campaign ${matchingCampaign.id} (${matchingCampaign.name}):`, error);
            }
        }
    }
}
async function getAllOffers() {
    const { data: offers } = await http_1.default.get('offers');
    return offers;
}
async function findKeitaroOffers(filter) {
    let offers = await getAllOffers();
    if (filter.caption)
        offers = offers.filter((o) => o.name.includes(filter.caption));
    if (filter.keitaroId)
        offers = offers.filter((o) => o.id == filter.keitaroId);
    if (filter.name)
        offers = offers.filter((o) => o.name.includes(filter.name));
    return offers;
}
async function getOfferByKeitaroId(id) {
    const { data: offer } = await http_1.default.get(`offers/${id}`);
    return offer;
}
async function updateOffer(offer) {
    const { data: o } = await http_1.default.put(`offers/${offer.id}`, offer);
    return o;
}
function createStreamPartialPayload(keitaroOfferId, offerName, offerId, offerGeo) {
    return {
        name: `${offerName} ${offerGeo} (${offerId})`,
        schema: "landings",
        type: "regular",
        action_type: "http",
        weight: 100,
        offers: [{
                offer_id: keitaroOfferId,
                share: 100,
                state: "active"
            }],
        filters: [{
                name: "sub_id_15",
                mode: "accept",
                payload: [offerId]
            }],
    };
}
async function addOffersToKeitaro(offers, affiliateId, links, avoidGroup, groupId) {
    const allOffers = await getAllOffers();
    const streamPayloads = [];
    for (let i = 0; i < offers.length; i++) {
        const offer = offers[i];
        const link = links[i];
        const identicalOffer = allOffers.find(o => o.name.includes(offer.name));
        let keitaroOfferId;
        if (identicalOffer) {
            keitaroOfferId = identicalOffer.id;
        }
        else {
            const offerPayload = {
                name: `${offer.caption} ${offer.geo} (${offer.name})`,
                group_id: groupId || 1,
                action_payload: link,
                affiliate_network_id: affiliateId,
                country: [offer.geo],
                action_type: "http",
                offer_type: "external",
                payout_auto: true,
                payout_upsell: true,
            };
            const { data: keitaroOffer } = await http_1.default.post('offers', offerPayload);
            keitaroOfferId = keitaroOffer.id;
        }
        streamPayloads.push({
            payload: createStreamPartialPayload(keitaroOfferId, offer.caption, offer.name, offer.geo),
            offerId: offer.name
        });
    }
    await createStreamForMatchingCampaigns(streamPayloads, /◈/, avoidGroup);
}
async function createCampaign(campaignData) {
    let { data: campaign } = await http_1.default.post(`/campaigns`, campaignData);
    return campaign;
}
async function getCampaignById(id) {
    const { data: campaign } = await http_1.default.get(`/campaigns/${id}`);
    return campaign;
}
async function upsertStreamToCampaign(campaign, stream) {
    let streams = await getStreamsByCampaignId(campaign.id);
    let identicalStream = streams.find((s) => stream.name == s.name);
    if (identicalStream) {
        console.log(`Found identical! Name: ${stream.name}`);
        await http_1.default.put(`streams/${identicalStream.id}`, {
            campaign_id: campaign.id,
            ...stream
        });
    }
    else {
        await http_1.default.post('streams', {
            campaign_id: campaign.id,
            ...stream
        });
    }
}
exports.upsertStreamToCampaign = upsertStreamToCampaign;
async function cloneOWCampaign(app, platform) {
    let name = `#${app.id} [◈]`;
    const appPlatform = getOfferwallAppPlatform(app);
    const templateCampaign = resolveOfferwallTemplateCampaign(appPlatform);
    let platformName = templateCampaign.name;
    const canonicalName = (0, app_campaign_naming_1.buildAppCampaignName)(app);
    const platformCampaignName = canonicalName || `#${app.id} [◈] (${platformName})`;
    const generateAlias = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        return Array.from({ length: 8 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
    };
    let allCampaigns = await getAllCampaigns();
    let matchingCampaign = [];
    matchingCampaign = allCampaigns.filter((c) => canonicalName
        ? c.name === canonicalName
        : c.name.includes(`#${app.id}`) && c.name.includes(`[◈]`) && c.name.includes(`(${platformName})`));
    if (matchingCampaign.length > 0)
        return matchingCampaign[0];
    const originalCampaign = await getCampaignById(templateCampaign.id);
    const maxPosition = allCampaigns.reduce((max, campaign) => Math.max(max, Number(campaign.position) || 0), 0);
    const originalStreams = await getStreamsByCampaignId(templateCampaign.id);
    const allDomains = await exports.KeitaroService.getDomains(true) || [];
    const cloneRouting = resolveOfferwallCloneRouting(originalCampaign, allDomains);
    let alias = generateAlias();
    let payload = {
        // Унікальні поля
        name: platformCampaignName,
        alias: alias,
        position: maxPosition + 100,
        ...cloneRouting,
        parameters: (0, keitaro_utils_1.prepareOWCampaignParameters)(app),
        // Неунікальні поля з оригінальної кампанії
        type: originalCampaign.type,
        state: originalCampaign.state,
        cost_type: originalCampaign.cost_type,
        cost_value: originalCampaign.cost_value,
        cost_currency: originalCampaign.cost_currency,
        uniqueness_period: originalCampaign.uniqueness_period,
        cookies_ttl: originalCampaign.cookies_ttl,
        notes: originalCampaign.notes,
        collect_clicks: originalCampaign.collect_clicks,
        uniqueness_type: originalCampaign.uniqueness_type,
    };
    const newCampaign = await createCampaign(payload);
    for (const stream of originalStreams) {
        await http_1.default.post('streams', {
            name: stream.name,
            campaign_id: newCampaign.id,
            schema: stream.schema,
            type: stream.type,
            action_type: stream.action_type,
            weight: stream.weight,
            offers: stream.offers.map((offer) => ({
                offer_id: offer.offer_id,
                share: offer.share,
                state: offer.state
            })),
            filters: stream.filters.map((filter) => ({
                name: filter.name,
                mode: filter.mode,
                payload: filter.payload
            })),
            position: stream.position,
            state: stream.state
        });
    }
    const updatedCampaign = await getCampaignById(newCampaign.id);
    return updatedCampaign;
}
async function cloneDirectCampaign(app, platform, addDefaultStreams) {
    const canonicalName = (0, app_campaign_naming_1.buildAppCampaignName)(app);
    let name = canonicalName || `#${app.id} [➥]`;
    let platformName = platform ? (0, app_1.getPlatformName)(platform) : null;
    const platformCampaignName = canonicalName || `#${app.id} [➥] (${platformName})`;
    const generateAlias = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        return Array.from({ length: 8 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
    };
    let allCampaigns = await getAllCampaigns();
    let matchingCampaign = [];
    if (canonicalName) {
        matchingCampaign = allCampaigns.filter((c) => c.name === canonicalName);
    }
    else if (platform && platform !== app_1.EPlatform.GENERAL) {
        // Для конкретної платформи
        const platformName = (0, app_1.getPlatformName)(platform);
        matchingCampaign = allCampaigns.filter((c) => {
            // Точна перевірка ID з межами слів
            const idPattern = new RegExp(`#${app.id}\\b`);
            const hasId = idPattern.test(c.name);
            // Перевіряємо наявність стрілки (може бути в комбінації з іншими символами)
            const hasArrow = c.name.includes('➥');
            // Перевіряємо наявність платформи в дужках в кінці назви
            const platformPattern = new RegExp(`\\(.*${platformName}.*\\)\\s*$`);
            const hasPlatform = platformPattern.test(c.name);
            return hasId && hasArrow && hasPlatform;
        });
    }
    else {
        // Для General платформи
        matchingCampaign = allCampaigns.filter((c) => {
            // Точна перевірка ID з межами слів
            const idPattern = new RegExp(`#${app.id}\\b`);
            const hasId = idPattern.test(c.name);
            // Перевіряємо наявність стрілки (може бути в комбінації з іншими символами)
            const hasArrow = c.name.includes('➥');
            // Перевіряємо відсутність платформи в дужках в кінці
            // Дозволяємо будь-які інші дужки (як для гео або опису)
            const noPlatformAtEnd = !/\(.*(?:iOS|Android|Desktop|Mobile).*\)\s*$/.test(c.name);
            return hasId && hasArrow && noPlatformAtEnd;
        });
    }
    if (matchingCampaign.length > 0) {
        const existingCampaign = matchingCampaign[0];
        // Якщо addDefaultStreams === true, додаємо потоки до існуючої кампанії
        if (addDefaultStreams) {
            const originalStreams = await getStreamsByCampaignId(3175);
            const existingStreams = await getStreamsByCampaignId(existingCampaign.id);
            for (const stream of originalStreams) {
                // Перевіряємо чи потік вже існує
                const existingStream = existingStreams.find(s => s.name === stream.name);
                if (!existingStream) {
                    await http_1.default.post('streams', {
                        name: stream.name,
                        campaign_id: existingCampaign.id,
                        schema: stream.schema,
                        type: stream.type,
                        action_type: stream.action_type,
                        weight: stream.weight,
                        offers: stream.offers.map((offer) => ({
                            offer_id: offer.offer_id,
                            share: offer.share,
                            state: offer.state
                        })),
                        filters: stream.filters.map((filter) => ({
                            name: filter.name,
                            mode: filter.mode,
                            payload: filter.payload
                        })),
                        position: stream.position,
                        state: stream.state
                    });
                }
            }
        }
        return existingCampaign;
    }
    const originalCampaign = await getCampaignById(3175);
    const maxPosition = Math.max(...allCampaigns.map(c => c.position || 0));
    const originalStreams = await getStreamsByCampaignId(3175);
    let allDomains = await exports.KeitaroService.getDomains(true);
    if (!allDomains) {
        throw Error(`Failed to get all domains list`);
    }
    const domain = allDomains[Math.floor(Math.random() * allDomains.length)];
    const maxGroupId = Math.max(...allCampaigns.map(c => c.group_id || 0));
    let alias = generateAlias();
    let payload = {
        // Унікальні поля
        name: platformName ? platformCampaignName : name,
        alias: alias,
        domain_id: domain.id,
        position: [maxPosition + 100],
        group_id: maxGroupId + 1,
        traffic_source_id: keitaro_utils_1.TRAFFIC_SOURCE_ID_FLASH_AI,
        parameters: (0, keitaro_utils_1.prepareOWCampaignParameters)(app),
        source: originalCampaign.source,
        bind_visitors: "slo",
        // Неунікальні поля з оригінальної кампанії
        type: originalCampaign.type,
        state: originalCampaign.state,
        cost_type: originalCampaign.cost_type,
        cost_value: originalCampaign.cost_value,
        cost_currency: originalCampaign.cost_currency,
        uniqueness_period: originalCampaign.uniqueness_period,
        cookies_ttl: originalCampaign.cookies_ttl,
        notes: originalCampaign.notes,
        collect_clicks: originalCampaign.collect_clicks,
        uniqueness_type: originalCampaign.uniqueness_type,
    };
    const newCampaign = await createCampaign(payload);
    // Додаємо потоки: всі якщо addDefaultStreams === true, інакше тільки перший
    const streamsToAdd = addDefaultStreams ? originalStreams : originalStreams.slice(0, 1);
    for (const stream of streamsToAdd) {
        await http_1.default.post('streams', {
            name: stream.name,
            campaign_id: newCampaign.id,
            schema: stream.schema,
            type: stream.type,
            action_type: stream.action_type,
            weight: stream.weight,
            offers: stream.offers.map((offer) => ({
                offer_id: offer.offer_id,
                share: offer.share,
                state: offer.state
            })),
            filters: stream.filters.map((filter) => ({
                name: filter.name,
                mode: filter.mode,
                payload: filter.payload
            })),
            position: stream.position,
            state: stream.state
        });
    }
    await http_1.default.put(`/campaigns/${newCampaign.id}`, {
        group_id: originalCampaign.group_id
    });
    const updatedCampaign = await getCampaignById(newCampaign.id);
    return updatedCampaign;
}
async function cloneDCampaign(app) {
    let name = `D #${app.id} (${app.bundle})`;
    let allCampaigns = await getAllCampaigns();
    let matchingCampaign = allCampaigns.filter((c) => c.name.includes(`D #${app.id}`));
    if (matchingCampaign.length > 0)
        return matchingCampaign[0];
    const { data: campaigns } = await http_1.default.post(`/campaigns/2693/clone`);
    if (campaigns.length == 0)
        throw Error("Campaign cloning falied");
    let clonedCampaign = campaigns[0];
    let allDomains = await exports.KeitaroService.getDomains(true);
    if (!allDomains) {
        throw Error(`Failed to get all domains list`);
    }
    const domain = allDomains[Math.floor(Math.random() * allDomains.length)];
    let payload = {
        name: name,
        traffic_source_id: keitaro_utils_1.TRAFFIC_SOURCE_ID_FLASH_AI,
        domain_id: domain.id,
        group_id: 93,
    };
    const { data: campaign } = await http_1.default.put(`/campaigns/${clonedCampaign.id}`, payload);
    return campaign;
}
async function changeCampaignsGroup(fromId, toId, exceptForCampaignIds, onlyForCampaignIds) {
    let campaigns = await getAllCampaigns();
    const matchingCampaigns = campaigns.filter(campaign => campaign.group_id == fromId
        && exceptForCampaignIds.length > 0 ? !exceptForCampaignIds.includes(campaign.id) : onlyForCampaignIds?.includes(campaign.id));
    for (let campaign of matchingCampaigns) {
        await updateCampaign(campaign.id, { group_id: toId });
    }
}
// Domain
async function getDomains(onlyActive) {
    const { data: domains } = await http_1.default.get(`/domains`);
    return onlyActive ? domains.filter((d) => d.network_status == "active") : domains;
}
async function getProfitForTimeRange(from, to) {
    let fromString = (0, general_1.convertMillisToDate)(from);
    let toString = (0, general_1.convertMillisToDate)(to);
    let { data: data } = await http_1.default.post(`/report/build`, {
        range: {
            from: fromString,
            to: toString,
            timezone: 'Europe/Kyiv'
        },
        metrics: ['profit']
    });
    return data.rows[0].profit;
}
// async function getProfitForTodayAndYesterday(): Promise<any> {
//     let timestamps = getTimestampsForTodayAndYesterday()
//     let today = await KeitaroService.getProfitForTimeRange(timestamps.today.start, timestamps.today.end)
//     let yesterday = await KeitaroService.getProfitForTimeRange(timestamps.yesterday.start, timestamps.yesterday.end)
//     return {
//         today: today,
//         yesterday: yesterday
//     }
// }
async function fixBrokenClickCosts(startDate, endDate) {
    // Get all campaigns to update
    const campaigns = await getAllCampaigns();
    const campaignIds = campaigns.map(campaign => campaign.id);
    // Prepare the payload for the API request
    const payload = {
        campaign_ids: campaignIds,
        costs: [{
                start_date: startDate,
                end_date: endDate,
                cost: 0,
                filters: {}
            }],
        timezone: "Europe/Kyiv",
        currency: "USD",
        only_campaign_uniques: 0
    };
    // Make the API request to update costs
    await http_1.default.post('/clicks/update_costs', payload);
}
async function getClicks(request) {
    const defaultRequest = typeof request === 'string' ? {
        range: { interval: request }
    } : {
        ...request
    };
    const { data } = await http_1.default.post('/clicks/log', defaultRequest);
    return data.rows;
}
exports.KeitaroService = {
    getStreamsByCampaignId, updateCampaign, getAllCampaigns, getAllOffers, cloneStreams, addOffersToKeitaro, getOfferByKeitaroId, getDomains, createCampaign, getCampaignById, upsertStreamToCampaign, cloneOWCampaign,
    updateOffer, changeCampaignsGroup, getProfitForTimeRange, getClicks,
    // getProfitForTodayAndYesterday, 
    cloneDCampaign, findKeitaroOffers, fixBrokenClickCosts, cloneDirectCampaign
};
